import { z } from 'zod';
import { MEMBERSHIP_ROLES, ROLE_RANK, type MembershipRole } from './team.js';

/**
 * الحساباتُ الفرعيّة — «أشتغل سيلز بنفس الدخول، كحسابٍ مستقلّ».
 *
 * المالكُ يضيف لأيّ عضوٍ — ولنفسه — حسابًا فرعيًّا أو أكثر من شاشة
 * المستخدمين. والحسابُ الفرعيّ مستخدمٌ مستقلّ: له اسمه ودوره وملفُّ صلاحياته
 * ومسمّاه، ويظهر في قوائم المسؤولين، وعملاؤه وصفقاته له وحده — لكنّه بلا
 * كلمة مرور ولا دخول مباشر. صاحبُه يدخل ببريده ثمّ يبدّل إليه من قائمة
 * الحساب، ويرجع بنقرة.
 *
 * والدورُ لا يعلو أبدًا على الدور الحقيقيّ لصاحبه: يُفحص عند الحفظ وعند
 * التبديل وفي كلّ طلبٍ على الخادم — فلا يُوثَق بالواجهة.
 */

/** حسابٌ فرعيّ كما تعرضه الواجهة. */
export interface SubAccountView {
  /** معرّفُ العضويّة. */
  id: string;
  /** معرّفُ مستخدمه — هو ما يُبدَّل إليه ويُسنَد إليه العملاء. */
  userId: string;
  name: string;
  role: MembershipRole;
  profileId: string | null;
  profileName: string | null;
  jobTitle: string | null;
}

/** ما تعرفه الواجهةُ عن وضع العمل — يصل مع /auth/me. */
export interface WorkModeView {
  /** الدورُ الحقيقيّ للحساب الأصليّ (user.role هو الفعّالُ الآن). */
  realRole: MembershipRole;
  /** اسمُ الحساب الأصليّ. */
  realName: string;
  /** الحسابُ الفرعيّ النشط — null: يعمل بحسابه الأصليّ. */
  active: SubAccountView | null;
  /** حساباتُه الفرعيّة التي أضافها له المالك. */
  profiles: SubAccountView[];
}

/** أقصى عدد الحسابات الفرعيّة للعضو الواحد. */
export const WORK_PROFILES_MAX = 10;

/** الأدوارُ التي يجوز أن يعمل بها حسابٌ فرعيّ لمن دورُه `real` — كلُّ ما دونه، بلا المالك. */
export function workProfileRoles(real: MembershipRole): MembershipRole[] {
  return MEMBERSHIP_ROLES.filter((r) => r !== 'OWNER' && ROLE_RANK[r] <= ROLE_RANK[real]);
}

/** هل يجوز لحسابٍ فرعيٍّ بالدور `role` أن يتبع من دورُه الحقيقيّ `real`؟ */
export function workRoleAllowed(real: MembershipRole, role: MembershipRole): boolean {
  return role !== 'OWNER' && ROLE_RANK[role] <= ROLE_RANK[real];
}

const Id = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

export const SubAccountInputSchema = z.object({
  name: z.string().trim().min(2, 'الاسم قصير.').max(120, 'الاسم طويل.'),
  role: z.enum(MEMBERSHIP_ROLES, { errorMap: () => ({ message: 'دور غير صالح.' }) }),
  profileId: Id.nullable().default(null),
  jobTitle: z.string().trim().max(120).nullable().default(null),
});
export type SubAccountInput = z.infer<typeof SubAccountInputSchema>;

/** التبديل: معرّفُ مستخدم الحساب الفرعيّ، أو null للرجوع إلى الأصليّ. */
export const SwitchWorkProfileSchema = z.object({
  id: Id.nullable(),
});
export type SwitchWorkProfileInput = z.infer<typeof SwitchWorkProfileSchema>;
